import React, {
  useMemo,
  useRef,
  useCallback,
  useContext,
  useState,
} from "react";
import { InfiniteQueryResult } from "react-query";
import { useTranslation } from "react-i18next";
import { AgGridReactProps } from "@ag-grid-community/react";
import { GridApi, RowNode } from "@ag-grid-community/core";

import UserContext from "contexts/userContext";
import { DataGrid, showNotification, ExportPictusJson } from "fe-shared/src/components";
import ExportExcelData from "fe-shared/src/components/ExportExcelData";
import { CloseIcon } from "fe-shared/src/components/Icons";
import AdditionalFilters from "components/AdditionalFilters";
import AssigneeBulkSelect from "components/AssigneeBulkSelect";
import { GridTransactionInternal } from "types";
import {
  getRequestParams,
  getFlattenedTransactions,
} from "fe-shared-transactions/src/helpers/transactionsRequestConverter";
import { getTransactionRowHeight } from "fe-shared-transactions/src/helpers/transactions";
import useUpdateEffect from "fe-shared/src/hooks/useUpdateEffect";
import useTransactionsExport from "fe-shared-transactions/src/hooks/useTransactionsExport";
import usePictusJsonExport from "fe-shared-transactions/src/hooks/usePictusJsonExport";
import {
  DataGridFilters,
  DataGridOptions,
} from "fe-shared/src/components/DataGrid/modules/types";
import { hasPermission } from "fe-shared/src/helpers/permissions";
import { PermissionLevel } from "fe-shared/src/types/domain";
import {
  GridTransaction,
  GridTransactionList,
  TransactionStatus,
} from "fe-shared-transactions/src/types";
import { PERMISSIONS } from "consts";

import useTenantOptions from "fe-shared/src/hooks/useTenantOptions";

import { getNavigationTransactions } from "routes/TransactionDetails/modules/api";
import CreateRetreadingTransaction from "../CreateRetreadingTransaction";
import { getColumnDefs, getRenderers } from "./modules/config";
import { isPictusJsonExportAvailable } from "./modules/pictusExportAvailability";

import styles from "./TransactionsDataGrid.module.scss";

interface Props extends AgGridReactProps {
  transactions: InfiniteQueryResult<
    GridTransactionList<GridTransactionInternal>
  >;
  lastOpenedNumber: string | null;
  clearLastOpenedNumber: () => void;
  options: DataGridOptions;
  filters: DataGridFilters;
  setOptions: (options: DataGridOptions) => void;
  setFilters: (filters: DataGridFilters) => void;
}

const BULK_ASSIGN_STATUSES = [
  TransactionStatus.Submitted,
  TransactionStatus.InReview,
  TransactionStatus.Reviewed,
  TransactionStatus.Suspended,
];

const TransactionsDataGrid = (props: Props) => {
  const {
    lastOpenedNumber,
    clearLastOpenedNumber,
    transactions,
    filters,
    ...otherProps
  } = props;

  const { t } = useTranslation();
  const user = useContext(UserContext);
  const gridApiRef = useRef<GridApi | null>(null);
  const columnDefs = useMemo(() => getColumnDefs(t), [t]);
  const [selectedRows, setSelectedRows] = useState<GridTransaction[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const userName = `${user?.firstName} ${user?.lastName}`;
  const requestParams = getRequestParams(filters, otherProps.options);
  const { columnConfig, getDataRows } = useTransactionsExport(requestParams);
  const { getExportParams } = usePictusJsonExport(requestParams);

  const [tenantOptions] = useTenantOptions();

  const canExport = hasPermission(user?.permissions, {
    name: PERMISSIONS.transactionsListExport,
    level: PermissionLevel.Full,
  });
  const canPictusExport = hasPermission(user?.permissions, {
    name: PERMISSIONS.transactionListPictusExport,
    level: PermissionLevel.Full,
  });
  const canAssign = hasPermission(user?.permissions, {
    name: PERMISSIONS.transactionListAssign,
    level: PermissionLevel.Full,
  });
  const canCreateRetreading = hasPermission(user?.permissions, {
    name: PERMISSIONS.transactionsListCreateRetreadingTransaction,
    level: PermissionLevel.Full,
  });

  const isPictusJsonExportable = isPictusJsonExportAvailable(
    canPictusExport,
    tenantOptions.webOptionFlags?.ExportPictusJSON === true,
    filters
  );

  const renderers = useMemo(() => getRenderers(canAssign), [canAssign]);

  const assignGridApi = (api: GridApi) => {
    gridApiRef.current = api;
  };

  const onFirstDataRendered = useCallback(() => {
    if (lastOpenedNumber) {
      gridApiRef.current!.ensureNodeVisible(
        (node: RowNode) => node.id === lastOpenedNumber.toString(),
        "middle"
      );

      clearLastOpenedNumber();
    }
  }, [lastOpenedNumber, clearLastOpenedNumber]);

  const rowCount = transactions.data?.[0].total || 0;

  const rowData = useMemo(() => getFlattenedTransactions(transactions.data), [
    transactions.data,
  ]);
  const updateFilters = (filtersUpdate: DataGridFilters) => {
    gridApiRef.current?.setFilterModel({
      ...filters,
      ...filtersUpdate,
    });
  };

  const onSelfAssignSuccess = () => {
    updateFilters({
      assignee: {
        type: "textFilter",
        value: userName,
      },
      statusCode: {
        type: "textFilter",
        value: TransactionStatus.InReview,
      },
    });
  };

  const getTransactionIds = async () => {
    if (isAllSelected && selectedRows.length < rowCount) {
      return getNavigationTransactions("", requestParams, null).then((res) =>
        res.transactionsIds.map((tr) => tr.transactionId)
      );
    }
    return Promise.resolve(selectedRows.map((tr) => tr.transactionId));
  };

  const onExportError = (error: any) => {
    showNotification(
      error.response?.data.message || error.message,
      undefined,
      CloseIcon
    );
  };

  const isAssignable =
    selectedRows.length > 1 &&
    (isAllSelected && selectedRows.length < rowCount
      ? BULK_ASSIGN_STATUSES.includes(
          filters.statusCode?.value as TransactionStatus
        )
      : selectedRows.every((tr) =>
          BULK_ASSIGN_STATUSES.includes(tr.statusCode)
        ));

  useUpdateEffect(() => {
    if (!transactions.isFetchingMore && isAllSelected) {
      gridApiRef.current?.selectAll();
    }
  }, [transactions.isFetchingMore]);

  return (
    <main className={styles.container}>
      <div className={styles.headerBlock}>
        <h1 className={styles.header}>
          {t("transactions.grid.title")}
          {!!rowCount && <span className={styles.totalCount}>{rowCount}</span>}
        </h1>
        <div className={styles.buttons}>
          {canCreateRetreading && <CreateRetreadingTransaction />}
          <AssigneeBulkSelect
            isAssignable={isAssignable}
            getIds={getTransactionIds}
            onSelfAssignSuccess={onSelfAssignSuccess}
          />
          {canExport && (
            <ExportExcelData
              dataRows={[]}
              columnConfig={columnConfig}
              getDataRows={getDataRows}
              onError={onExportError}
              buttonId="export-transactions-button"
              fileName={t("transactions.grid.export.fileName")}
              buttonLabel={t("transactions.grid.export.buttonLabel")}
            />
          )}
          {isPictusJsonExportable && (
            <ExportPictusJson
              getExportParams={getExportParams}
              userEmail={user?.email || ""}
              onError={onExportError}
              buttonId="export-pictus-json-button"
              buttonLabel={t("transactions.grid.export.pictusJsonButtonLabel")}
            />
          )}
          <AdditionalFilters filters={filters} updateFilters={updateFilters} />
        </div>
      </div>
      <DataGrid
        idField="transactionNumber"
        rowSelection="multiple"
        suppressRowClickSelection
        suppressCellSelection
        assignGridApi={assignGridApi}
        onFirstDataRendered={onFirstDataRendered}
        columnDefs={columnDefs}
        isLoading={transactions.isLoading}
        rowData={rowData}
        fetchMore={transactions.fetchMore}
        canFetchMore={transactions.canFetchMore}
        isFetchingMore={transactions.isFetchingMore}
        className={styles.transactionsDataGrid}
        frameworkComponents={renderers}
        filters={filters}
        getRowHeight={getTransactionRowHeight}
        onSelectionChanged={(e) => {
          const rows = e.api.getSelectedRows();

          setSelectedRows(rows);
          setIsAllSelected(!!rows.length && rows.length === rowData?.length);
        }}
        {...otherProps}
      />
    </main>
  );
};

export default TransactionsDataGrid;
